import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import LegalLayout from '../components/legal/LegalLayout';

const ITEMS = [
  {
    q: 'I didn’t get my ticket. What should I do?',
    a: 'Use “Find My Pass” if available and enter the email or phone number used during registration. If the event requires approval, your pass may not unlock until the Host approves it.',
  },
  {
    q: 'What does “Pending Approval” mean?',
    a: 'Some events require Host approval before a pass becomes active. Once approved, you may receive an updated confirmation or access link.',
  },
  {
    q: 'Does a pass guarantee entry?',
    a: 'No. VelvetGate provides the access technology, but the Host and venue control admission. A pass may still be denied, revoked, or rejected based on event rules.',
  },
  {
    q: 'Can I send my ticket to someone else?',
    a: 'That depends on the Host’s rules. If ticket claiming or transfer is enabled, the new guest may need to claim the pass using a code or link.',
  },
  {
    q: 'I entered the wrong email or phone number. What now?',
    a: 'Use “Find My Pass” if available or contact the event Host. If it is a platform issue, contact VelvetGate support.',
  },
  {
    q: 'Do I need to print my ticket?',
    a: 'No. Most passes can be scanned directly from a phone. Printed passes may work if the QR code is clear and still valid.',
  },
  {
    q: 'What if my pass was revoked?',
    a: 'A revoked pass is no longer valid for entry. Revocations are controlled by the Host. Contact the Host for event-specific questions.',
  },
  {
    q: 'Who handles refunds?',
    a: 'Refunds are handled by the event Host unless VelvetGate clearly states otherwise. Check the event’s details or contact the Host directly.',
  },
  {
    q: 'Why does my ticket show as used?',
    a: 'A pass may show as used after it has been scanned at check-in. If you believe this is incorrect, contact the Host or event staff.',
  },
  {
    q: 'Who do I contact for help?',
    a: 'For event-specific questions, contact the Host. For platform-related issues, contact VelvetGate at admin@velvetgatepass.com.',
  },
  {
    q: 'How do I get access to premium features?',
    a: 'VelvetGate organizes features into tiers (Starter, Pro, Premium). Starter is the default for every account and covers the core hosting flow. Advanced report filters, staff management, and access codes require Pro. Custom branding requires Premium. If you’ve received a promotional code or QR from VelvetGate, visit /redeem or scan the QR to unlock access instantly — no billing involved.',
  },
  {
    q: 'What do Pro and Premium unlock?',
    a: 'Pro unlocks advanced insights and reporting tools — advanced filters, Guest Attendance, expanded exports, Post Event Insights, saved snapshots, access codes, and discount codes. Premium unlocks branding and identity customization on top of everything in Pro.',
  },
  {
    q: 'Can I try Pro features?',
    a: 'Yes — access may be granted via promotional codes issued by VelvetGate. Scan the QR or visit /redeem with the code to start a trial.',
  },
  {
    q: 'What happens when my trial ends?',
    a: 'When a trial or promotional access expires, your account returns to its underlying tier automatically. Your events, guests, tickets, reports, and data are never touched — only the premium feature visibility changes. You can re-redeem another code at any time.',
  },
];

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <LegalLayout title="Help & FAQ" testId="faq-page">
      <p>Need help with a pass, approval, or event access? Start here.</p>
      <div className="mt-4 p-4 border border-[#D4AF37]/25 rounded-sm bg-[#0A0A0A] flex items-start gap-3" data-testid="faq-find-pass-cta">
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#F3EEE7]">Didn&rsquo;t get your ticket?</div>
          <p className="text-xs text-[#B9B1A6] mt-0.5">Recover a lost or missing pass with your registration email.</p>
        </div>
        <a
          href="/find-pass"
          className="flex-shrink-0 inline-flex items-center justify-center px-3 py-2 rounded-sm bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] text-xs font-bold transition-opacity"
          data-testid="faq-find-pass-link"
        >
          Find My Pass
        </a>
      </div>
      <div className="space-y-2 mt-4">
        {ITEMS.map((item, i) => {
          const open = openIdx === i;
          return (
            <div key={i} className="border border-[#1A1A1A] rounded-sm bg-[#0A0A0A] overflow-hidden" data-testid={`faq-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpenIdx(open ? -1 : i)}
                className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 hover:bg-[#111] transition-colors"
                aria-expanded={open}
                data-testid={`faq-question-${i}`}
              >
                <span className="text-sm sm:text-base font-semibold text-[#EAE7E1]">{item.q}</span>
                <ChevronDown className={`w-4 h-4 text-[#D4AF37] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>
              {open && (
                <div className="px-4 pb-4 text-sm text-[#B9B1A6] leading-relaxed" data-testid={`faq-answer-${i}`}>
                  {item.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[#777] mt-6">
        Still stuck? <a href="/contact" className="text-[#D4AF37] hover:underline">Contact VelvetGate</a>.
      </p>
    </LegalLayout>
  );
}
